import { z } from "zod";
import {
  Box,
  Text,
  WidgetInteractive,
  WidgetSkeleton,
  compactNumber,
  useWidgetInstance,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import { widgetFiltersSchema } from "#app/widgets/dashboards/widget-filters";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import { WidgetHeaderFilters } from "../shared/widget-header-filters";
import { useProjectsClosed } from "./use-projects-closed";

export const projectsClosedConfigSchema = z
  .looseObject({
    includeLost: z.boolean().default(false),
    compact: z.boolean().default(false),
    filters: widgetFiltersSchema.nullish(),
  });

export type ProjectsClosedConfig = z.infer<typeof projectsClosedConfigSchema>;

function ProjectsClosedRender({ h, config }: WidgetRenderContext<ProjectsClosedConfig>) {
  const { value, doneIds, loading, error } = useProjectsClosed(config.includeLost, config.filters);
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();

  if (error) throw error;
  if (loading) {
    return <WidgetSkeleton variant="stat" />;
  }

  const canDrill = Boolean(drill && instance && doneIds.length > 0);
  const openData = () => {
    if (!drill || !instance || doneIds.length === 0) return;
    drill.openData(instance.instanceId, {
      kind: "dimension",
      dimension: "stage",
      ids: doneIds,
      label: "Closed",
    });
  };

  return (
    <Box fill="flex" align="center" justify="center" paddingY="xs">
      <WidgetInteractive
        fill="cell"
        rounded="lg"
        align="center"
        justify="center"
        cursor={canDrill ? "pointer" : undefined}
        hoverBackground={canDrill}
        onClick={canDrill ? openData : undefined}
      >
        <Box align="center" gap="2xs">
          <Text size={h >= 3 ? "4xl" : "2xl"} weight="bold" truncate={1}>
            {config.compact ? compactNumber(value) : value.toLocaleString()}
          </Text>
          <Text size="sm" variant="muted" truncate={1}>
            closed projects
          </Text>
        </Box>
      </WidgetInteractive>
    </Box>
  );
}

const projectsClosedSettings: WidgetSettingsGroup[] = [
  {
    title: "Data",
    fields: [
      { key: "includeLost", kind: "toggle", label: "Count lost/canceled too", icon: "x-circle" },
    ],
  },
  {
    title: "Display",
    fields: [
      { key: "compact", kind: "toggle", label: "Abbreviate large numbers", icon: "minimize-2" },
    ],
  },
  {
    title: "Filters",
    fields: [{ key: "filters", kind: "issueFilters", label: "Filters" }],
  },
];

export const projectsClosedDef: WidgetModule<ProjectsClosedConfig> = {
  type: "projects-closed",
  title: "Projects closed",
  icon: "check-circle",
  span: { minW: 2, minH: 2, maxW: 12, maxH: 4, defaultW: 3, defaultH: 2 },
  configSchema: projectsClosedConfigSchema,
  settings: projectsClosedSettings,
  headerActions: WidgetHeaderFilters,
  bodyDraggable: true,
  catalog: [
    {
      key: "projects-closed.default",
      label: "Projects closed",
      description: "Counts projects created in the window that are now in a Done stage.",
      section: "Charts",
      defaultConfig: { includeLost: false },
      defaultW: 3,
      defaultH: 2,
    },
  ],
  render: ProjectsClosedRender,
};
